import * as React from 'react';

export type ToastActionElement = React.ReactElement;

export interface ToastProps {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'default' | 'destructive' | 'snapshot';
  action?: ToastActionElement;
  duration?: number;
  persistent?: boolean;
  dismissible?: boolean;
}

export type Toast = Omit<ToastProps, 'id'>;

interface ToastProviderProps {
  children: React.ReactNode;
}

export interface ToastContextValue {
  toasts: ToastProps[];
  addToast: (toast: Toast) => string;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = React.useState<ToastProps[]>([]);

  const removeToast = React.useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const addToast = React.useCallback((toast: Toast) => {
    const id = Math.random().toString(36).slice(2, 9);
    setToasts((current) => [...current, { id, ...toast }]);
    return id;
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
